import React from 'react';
import { KpiStat } from './shared/Card';
import type { BillingSummaryKpis } from '../../services/billing';
import { formatMoneyDollars as money } from '../../utils/money';

interface KpiCardsProps {
    kpis: BillingSummaryKpis;
}

// The KPI recap row from billing-v1-notes.md, all-time (see BillingPage's
// summary query for why the 12-month window was dropped).
//
// The mockup's Outstanding card also has a
// "Pay $X →" CTA that pays every overdue invoice at once — the backend only
// exposes a per-invoice pay-link (POST .../invoices/:id/pay-link), so a batch
// "pay all" would mean silently opening one tab per invoice; omitted (a true
// pay-all needs a batch pay-link endpoint, follow-up). The card previously
// linked to a separate Invoices tab, but the table now sits directly below
// this row on the same page, so that link was dropped rather than replaced.
//
// "Available credits" was dropped from the row per user feedback (down to 4
// cards) — `kpis.creditsAvailable` still comes back from the API and stays
// in the service types; only this display was removed.
export const KpiCards: React.FC<KpiCardsProps> = ({ kpis }) => {
    const hasOverdue = kpis.overdueCount > 0;
    const paidRateLabel =
        kpis.paidRatePct !== null
            ? `${kpis.paidRatePct}% of what we billed`
            : 'No invoices billed yet';

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiStat
                label="Outstanding balance"
                value={money(kpis.outstanding)}
                tone={hasOverdue ? 'alert' : 'default'}
                sub={
                    hasOverdue
                        ? `${kpis.overdueCount} invoice${kpis.overdueCount === 1 ? '' : 's'} overdue · ${money(kpis.overdueAmount)}`
                        : `${kpis.outstandingCount} invoice${kpis.outstandingCount === 1 ? '' : 's'} awaiting payment`
                }
            />
            <KpiStat
                label="Billed · all time"
                info="Total amount of non-voided invoices ever created for your practice."
                value={money(kpis.billed)}
                sub={`across ${kpis.billedCount} invoice${kpis.billedCount === 1 ? '' : 's'}`}
            />
            <KpiStat
                label="Paid · all time"
                info="Total amount ever paid on your invoices."
                value={money(kpis.paid)}
                sub={paidRateLabel}
            />
            <KpiStat
                label="Estimated revenue · all time"
                info="An estimate — every treatment charge ever invoiced × 2.5, a rough proxy for what patients are typically billed. Not a guarantee."
                value={money(kpis.estimatedRevenue)}
                sub="Estimate, treatment charges only"
            />
        </div>
    );
};
